
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Camera, User, Image } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

interface AddChildFormProps {
  onAddChild: (child: { name: string; birthdate: string; photo?: File }) => Promise<void>;
}

const AddChildForm: React.FC<AddChildFormProps> = ({ onAddChild }) => {
  const [newChild, setNewChild] = useState({
    name: '',
    birthdate: ''
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (photo) {
      await onAddChild({ ...newChild, photo });
    } else {
      await onAddChild(newChild);
    }
    setNewChild({ name: '', birthdate: '' });
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handlePhotoClick = async () => {
    try {
      // Check if we're on a native platform
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        // Use native camera plugin - will show action sheet with Camera/Photos options
        const image = await CapacitorCamera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt, // Asks user: Camera or Photos
          quality: 80,
          width: 800, // Resize to reasonable size
          height: 800,
          allowEditing: true, // Let user crop/edit
        });

        if (image.dataUrl) {
          setPhotoPreview(image.dataUrl);

          // Convert data URL to File for upload
          const response = await fetch(image.dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'child-photo.jpg', { type: 'image/jpeg' });
          setPhoto(file);
        }
      } else {
        // Fallback to web file picker (for web/testing)
        const image = await CapacitorCamera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Photos,
          quality: 80,
          width: 800,
          height: 800,
          allowEditing: true,
        });

        if (image.dataUrl) {
          setPhotoPreview(image.dataUrl);

          const response = await fetch(image.dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'child-photo.jpg', { type: 'image/jpeg' });
          setPhoto(file);
        }
      }
    } catch (error) {
      // User cancelled or error occurred
      if (error instanceof Error && error.message !== 'User cancelled photos app') {
        toast({
          title: "Photo Error",
          description: "Could not access photos. Please check permissions.",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Add New Child</DialogTitle>
      </DialogHeader>
      <div className="grid gap-4 py-4">
        <div className="flex justify-center mb-2">
          <div 
            onClick={handlePhotoClick}
            className="relative cursor-pointer"
          >
            <Avatar className="h-24 w-24">
              {photoPreview ? (
                <AvatarImage src={photoPreview} alt="Preview" />
              ) : (
                <AvatarFallback className="bg-gray-200">
                  <User className="h-12 w-12 text-gray-400" />
                </AvatarFallback>
              )}
            </Avatar>
            <div className="absolute bottom-0 right-0 bg-primary rounded-full p-1">
              <Camera className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="name">Name</Label>
          <Input 
            id="name" 
            placeholder="Child's name"
            value={newChild.name}
            onChange={(e) => setNewChild({...newChild, name: e.target.value})}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="birthdate">Birthdate</Label>
          <Input 
            id="birthdate" 
            type="date" 
            value={newChild.birthdate}
            onChange={(e) => setNewChild({...newChild, birthdate: e.target.value})}
          />
        </div>
        <Button onClick={handleSubmit} className="mt-2">
          Add Child
        </Button>
      </div>
    </DialogContent>
  );
};

export default AddChildForm;
